import type {
  AdminUser,
  LoginUserInput,
  AuthResponse,
  ChangePasswordInput,
  WhiteLabelChangePasswordInput,
  ForgotPasswordInput,
  ResetPasswordInput,
  StatusResponse,
  DashboardResponse,
  PaginationInput,
  UsersResponse,
  ParamsSort,
  User,
  TransactionsResponse,
  TransfersResponse,
  BankCardsResponse,
  UserBalancesResponse,
  BankAccountsResponse,
  BankAccountInput,
  BankAccount,
  BankAccountUpdate,
  Google2FASecretKeyResponse,
  Notification,
  NotificationInput,
  AdminUsersResponse,
  AdminUserInput,
  AdminUserResponse,
  AdminUserUpdate,
  Permission,
  CurrencyResponse,
  CurrencyInput,
  Limitation,
  LimitationUpdate,
  DailyLimit,
  DailyLimitUpdate,
  LimitSettings,
  LimitSettingsUpdate,
  UpdateUmlStatus,
  UpdateUserVip,
  Fee,
  FeeUpdate,
  ExportDashboard,
  IbanResponse,
  TOurPricesResponse,
  IOurPricesItem,
  IGetOurPricesInput,
  IUpdateOurPricesInput,
  KwickpayHistoryResponse,
  IuserSettingsResponse,
  UnidentifyDepositResponse,
  IReset2FAResponse,
  IEmailHistoryResponse,
  ICreateOurPricesInput,
  MoneyMoversHistoryResponse,
  BillingCategoriesResponse,
  ParamsSort_1,
  MoneyMoverBalanceResponse,
  DailyLimitResponse,
  LimitationResponse,
  IMTSPaymentTransactionResponse,
  UserLimitation,
  UserDailyLimitResponse,
  BillingLimitationUpdate,
  ServiceParamsSort,
  ServiceParamsInput,
  moneyMoversCategoriesResponse,
  addCategoryInput,
  updateCategoryInput,
  moneyMoversServiceResponse,
  moneyMoversCategory,
  casinoServiceList,
  updateServiceInput,
  moneyMoversCashoutServiceResponse,
  LanguagesList,
  IModuleData,
  IModuleInput,
  ITranslations,
  IUpdateModuleInput,
  ConversionResponse,
  ConversionUpdate,
  ConversionListApiResponse,
  ProviderListResponse,
  SelfTerminalPaginationInput,
  PreLoginTransactionResponse,
  FeeResponse,
  AddPermissionInput,
  newPaginationInput,
  UpdateFrozenBalanceInput,
  ConversionResponseV2,
  ActiveChannelPartnerItem,
  ActiveRoleItem,
  RoleApiItem,
  CreateRoleInput,
  UpdateRoleInput,
  DuplicateRoleInput,
  ChannelPartnerApiItem,
  CreateChannelPartnerInput,
  UpdateChannelPartnerInput,
  WhiteLabelAdminItem,
  CreateWhiteLabelAdminInput,
  UpdateWhiteLabelAdminInput,
  TwoFAStatusResponse,
  Update2FAInput,
  Update2FAResponse,
  UpdateUserPhoneInput,
  UpdateUserPhoneResponse,
} from "./types";
import { API_ENDPOINTS } from "./endpoints";
import {
  HttpClient,
  HttpClient_1,
  HttpClient_2,
  HttpClient_AdminServiceV2,
  HttpClient_EmailTemplate,
  ManualTransactionsHttpClient,
  HttpClient_PermissionsModule,
} from "./http-client";
import { IBillingTransaction } from "src/lib/constants/table-type";
import { generateHmac } from "src/lib/utils";

class Client {
  auth = {
    login: async (input: LoginUserInput): Promise<AuthResponse> => {
      // const payload: any = {
      //   email: input.email,
      //   password: input.password,
      // };
      // if (input.totp) {
      //   payload.totp = input.totp;
      // }
      const response: any = await HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.USERS_LOGIN,
        input
      );

      const rawData = response?.data || response;
      const token = rawData?.token || response?.token;

      const rawPermissions =
        rawData?.role?.permissions || rawData?.permissions || [];
      const normalizedPermissions = rawPermissions.map((p: any) => ({
        id: p.permission_id ?? p.id,
        name: p.permission_name ?? p.name,
        permission_id: p.permission_id ?? p.id,
        permission_name: p.permission_name ?? p.name,
        permission_description: p.permission_description ?? null,
        can_add: Boolean(p.can_add),
        can_edit: Boolean(p.can_edit),
        can_list: Boolean(p.can_list),
        can_view: Boolean(p.can_view),
        can_delete: Boolean(p.can_delete),
        can_approve_reject: Boolean(p.can_approve_reject),
        can_export: Boolean(p.can_export),
        can_download: Boolean(p.can_download),
        can_block: Boolean(p.can_block),
      }));

      const normalizedUserData: AdminUser = {
        id: rawData?.id,
        email: rawData?.email,
        first_name: rawData?.first_name,
        last_name: rawData?.last_name,
        google2fa_enabled: rawData?.google2fa_enabled ? 1 : 0,
        last_login: rawData?.last_login,
        is_active: rawData?.is_active,
        last_activity: rawData?.last_activity,
        role_id: rawData?.role_id,
        is_different_channel_access: Boolean(
          rawData?.is_different_channel_access
        ),
        channel_partner_id: rawData?.channel_partner_id,
        created_at: rawData?.created_at,
        updated_at: rawData?.updated_at,
        token: token,
        role: rawData?.role
          ? {
            ...rawData.role,
            permissions: normalizedPermissions,
          }
          : undefined,
        permissions: normalizedPermissions,
      };

      return {
        token: token,
        user_data: normalizedUserData,
        data: normalizedUserData,
        status: response?.status,
        code: response?.code,
        message: response?.message,
        items: response?.items || [],
        totalcount: response?.totalcount || 0,
      };
    },
    getProfile: async (id?: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(
        API_ENDPOINTS.AUTH_PROFILE
      );
      return resData?.data || resData;
    },
    changePassword: async (
      input: ChangePasswordInput | WhiteLabelChangePasswordInput | any
    ) => {
      const payload = {
        oldPassword: input.oldPassword ?? input.old_password,
        newPassword: input.newPassword ?? input.password,
        confirmNewPassword:
          input.confirmNewPassword ?? input.password_confirmation,
      };
      const resData = await HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.AUTH_CHANGE_PASSWORD,
        payload
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message ||
          resData?.error_description ||
          "Failed to change password"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData;
    },
    logout: async () => {
      const resData = await HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.USERS_LOGOUT,
        {}
      );
      return resData;
    },
    forgotPassword: async (input: ForgotPasswordInput) => {
      const resData = await HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.AUTH_FORGOT_PASSWORD,
        input
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message ||
          resData?.error_description ||
          "Failed to send reset code"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData;
    },
    resetPassword: async (input: ResetPasswordInput) => {
      const resData = await HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.AUTH_RESET_PASSWORD,
        input
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message ||
          resData?.error_description ||
          "Failed to reset password"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData;
    },
  };
  dashboard = {
    getBalances: () =>
      HttpClient.get<DashboardResponse>(API_ENDPOINTS.GET_USERS_BALANCES),
    export: (input: ExportDashboard) =>
      HttpClient.get(API_ENDPOINTS.GET_DASHBORAD_EXPORT, input),
    getStartDate: (input: ExportDashboard) =>
      HttpClient.get(API_ENDPOINTS.GET_HISTORY_DAY, input),
    getMoneyMoversBalances: () =>
      HttpClient.get<MoneyMoverBalanceResponse>(
        API_ENDPOINTS.GET_USERS_MONEY_MOVER_BALANCES,
      ),
    getExport: (input: ExportDashboard) => {
      const { export_type, ...params } = input;
      return HttpClient.get(
        `${API_ENDPOINTS.DASHBOARD_EXPORT}/${export_type}`,
        params,
      );
    },
  };
  users = {
    getUsers: (input: PaginationInput) =>
      HttpClient.get<UsersResponse>(API_ENDPOINTS.GET_USERS, input),
    getIndividualUsers: (input: PaginationInput) =>
      HttpClient.get<UsersResponse>(API_ENDPOINTS.GET_INDIVIDUAL_USERS, input),
    getBuisinessUsers: (input: PaginationInput) =>
      HttpClient.get<UsersResponse>(API_ENDPOINTS.GET_BUISINESS_USERS, input),
    blockUser: (input: { id: number; blocked: number; type: string }) =>
      HttpClient.put<User>(
        `${API_ENDPOINTS.GET_USERS_BLCKED(String(input.type))}/${input.id}${API_ENDPOINTS.BLOCK_USER
        }`,
        { blocked: input.blocked },
      ),
    // blockUser: (input: {
    //   id: number;
    //   blocked: number;
    //   type: "business" | "individual";
    // }) =>
    //   HttpClient_EmailTemplate.put<User>(
    //     API_ENDPOINTS.GET_USERS_BLCKED(input.type, input.id),
    //     {
    //       blocked: input.blocked,
    //     },
    //   ),
    // updateVIPStatus: (input: { id: number; is_vip: number }) =>
    //   HttpClient.put<User>(
    //     `${API_ENDPOINTS.GET_USER}${input.id}${API_ENDPOINTS.UPDATE_VIP}`,
    //     { is_vip: input.is_vip },
    //   ),
    getIndividualTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_USER_TRANSACTIONS(input.id),
        input.params,
      ),
    getBuisinessTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_BUISINESS_USER_TRANSACTIONS(input.id),
        input.params,
      ),
    getBillingIndividualTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<IBillingTransaction>(
        API_ENDPOINTS.GET_INDIVIDUAL_USER_TRANSACTIONS(input.id),
        input.params,
      ),
    getBillingBuisinessTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<IBillingTransaction>(
        API_ENDPOINTS.GET_BUISINESS_USER_TRANSACTIONS(input.id),
        input.params,
      ),
    getMtsBuisinessTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<any>(
        API_ENDPOINTS.GET_BUISINESS_MTS_USER_TRANSACTIONS(input.id),
        input.params,
      ),
    getTransfers: (input: { id: number; params: PaginationInput }) =>
      HttpClient.get<TransfersResponse>(
        `${API_ENDPOINTS.GET_USER}${input.id}${API_ENDPOINTS.GET_USER_TRANSFERS}`,
        input.params,
      ),
    getBankCards: (input: { id: number; params: PaginationInput }) =>
      HttpClient.get<BankCardsResponse>(
        `${API_ENDPOINTS.GET_USER}${input.id}${API_ENDPOINTS.GET_USER_BANKCARDS}`,
        input.params,
      ),
    getIndividualBalances: (id: number) =>
      HttpClient.get<UserBalancesResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_USER_BALANCES(id),
      ),
    getBusinessBalances: (id: number) =>
      HttpClient.get<UserBalancesResponse>(
        API_ENDPOINTS.GET_BUISINESS_USER_BALANCES(id),
      ),
    updateFrozenBalances: (input: UpdateFrozenBalanceInput) =>
      HttpClient_EmailTemplate.post<Limitation>(
        API_ENDPOINTS.UPDATE_FROZEN_BALANCE,
        input,
      ),
    removeFrozenBalances: (input: UpdateFrozenBalanceInput) =>
      HttpClient_EmailTemplate.post<Limitation>(
        API_ENDPOINTS.REMOVE_FROZEN_BALANCE,
        input,
      ),
    removeMainBalances: (input: UpdateFrozenBalanceInput) =>
      HttpClient_EmailTemplate.post<Limitation>(
        API_ENDPOINTS.REMOVE_MAIN_BALANCE,
        input,
      ),
    getIndividualLimitations: (input: UserLimitation) => {
      const { id, ...params } = input;
      return HttpClient.get<LimitationResponse[]>(
        API_ENDPOINTS.GET_USER_INDIVIDUAL_LIMITATIONS(id),
        params,
      );
    },
    updateIndividualLimitation: (params: {
      input: LimitationUpdate;
      userId: number;
    }) =>
      HttpClient.put<Limitation>(
        API_ENDPOINTS.GET_USER_INDIVIDUAL_LIMITATIONS(params.userId),
        params.input,
      ),

    getDailyIndividualLimitations: (input: UserLimitation) => {
      const { id, ...params } = input;
      return HttpClient.get<UserDailyLimitResponse[]>(
        API_ENDPOINTS.GET_USER_DAILY_INDIVIDUAL_LIMITATIONS(id),
        params,
      );
    },

    updateDailyIndividualLimitation: (params: {
      input: DailyLimit;
      userId: number;
    }) =>
      HttpClient.put<Limitation>(
        API_ENDPOINTS.GET_USER_DAILY_INDIVIDUAL_LIMITATIONS(params.userId),
        params.input,
      ),
    getBuisnessLimitations: (input: UserLimitation) => {
      const { id, ...params } = input;
      return HttpClient.get<LimitationResponse[]>(
        API_ENDPOINTS.GET_USER_BUISNESS_LIMITATIONS(id),
        params,
      );
    },

    updateBuisnessLimitation: (params: {
      input: LimitationUpdate;
      userId: number;
    }) =>
      HttpClient.put<Limitation>(
        API_ENDPOINTS.GET_USER_BUISNESS_LIMITATIONS(params.userId),
        params.input,
      ),

    // getDailyIndividualLimitations: (input: UserLimitation) => {
    //   const { id, ...params } = input;
    //   return HttpClient.get<UserDailyLimitResponse[]>(
    //     API_ENDPOINTS.GET_USER_DAILY_INDIVIDUAL_LIMITATIONS(id),
    //     params
    //   );
    // },

    getDailyBuisnessLimitations: (input: UserLimitation) => {
      const { id, ...params } = input;
      return HttpClient.get<UserDailyLimitResponse[]>(
        API_ENDPOINTS.GET_USER_DAILY_BUISNESS_LIMITATIONS(id),
        params,
      );
    },
    updateDailyBuisnessLimitation: (params: {
      input: DailyLimitUpdate;
      userId: number;
    }) =>
      HttpClient.put<Limitation>(
        API_ENDPOINTS.GET_USER_DAILY_BUISNESS_LIMITATIONS(params.userId),
        params.input,
      ),
    getIndividualLimitSettings: (id: number) =>
      HttpClient.get<LimitSettings>(
        API_ENDPOINTS.USER_INDIVIDUAL_LIMITATION_SETTINGS(id),
      ),
    getBuisnessLimitSettings: (id: number) =>
      HttpClient.get<LimitSettings>(
        API_ENDPOINTS.USER_BUISNESS_LIMITATION_SETTINGS(id),
      ),
    updateIndividualLimitSettings: (params: {
      input: LimitSettingsUpdate;
      userId: number;
    }) =>
      HttpClient_EmailTemplate.put<LimitSettings>(
        API_ENDPOINTS.USER_LIMITATION_SETTINGS(params.userId),
        params.input,
      ),
    updateBuisnessLimitSettings: (params: {
      input: LimitSettingsUpdate;
      userId: number;
    }) =>
      HttpClient_EmailTemplate.put<LimitSettings>(
        API_ENDPOINTS.USER_LIMITATION_SETTINGS(params.userId),
        params.input,
      ),
    updateIndividualAmlStatus: (params: {
      input: UpdateUmlStatus;
      userId: number;
    }) =>
      HttpClient.put<any>(
        API_ENDPOINTS.UDPATE_USER_UML_STATUS(params.userId),
        params.input,
      ),
    updateBuisinessAmlStatus: (params: {
      input: UpdateUmlStatus;
      userId: number;
    }) =>
      HttpClient.put<any>(
        API_ENDPOINTS.UDPATE_BUISINESS_UML_STATUS(params.userId),
        params.input,
      ),
    //-------------------------------
    updateIndividualAgencyStatus: (params: { id: number }) =>
      HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_USER_AGENCY_STATUS,
        {
          userId: params.id, // Now passing ID in the body
        },
      ),
    // -------------------------------

    updateIndividualVip: (params: { input: UpdateUserVip; userId: number }) =>
      HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_USER_VIP_STATUS(params.userId),
        params.input,
      ),
    updateBuisinessVip: (params: { input: UpdateUserVip; userId: number }) =>
      HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_USER_VIP_STATUS(params.userId),
        params.input,
      ),
    //-------2FA update
    update2FAForUser: (params: {
      input: Update2FAInput;
      userId: number | string;
    }) =>
      HttpClient_PermissionsModule.put<Update2FAResponse>(
        API_ENDPOINTS.UPDATE_2FA_USER(params.userId),
        params.input,
      ),

    //-------User Phone update
    updateIndividualUserPhone: (params: {
      id: number | string;
      phone: string;
    }) =>
      HttpClient_PermissionsModule.put<UpdateUserPhoneResponse>(
        API_ENDPOINTS.UPDATE_INDIVIDUAL_USER(params.id),
        { phone: params.phone },
      ),
    updateBusinessUserPhone: (params: {
      id: number | string;
      phone: string;
    }) =>
      HttpClient_PermissionsModule.put<UpdateUserPhoneResponse>(
        API_ENDPOINTS.UPDATE_BUSINESS_USER(params.id),
        { phone: params.phone },
      ),

    getIndividualCardNumbers: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient.get<BankCardsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_BANK_CARDS(input.id),
        input.params,
      ),
    getBusinessCardNumbers: (input: { id: number; params: PaginationInput }) =>
      HttpClient.get<BankCardsResponse>(
        API_ENDPOINTS.GET_BUISINESS_BANK_CARDS(input.id),
        input.params,
      ),
    getIndividualIbanAccounts: (input: {
      id: number;
      account_type: string;
      params: PaginationInput;
    }) =>
      HttpClient.get<IbanResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_IBAN_ACCOUNTS(
          input.id,
          input.account_type,
        ),
        input.params,
      ),
    getBusinessIbanAccounts: (input: {
      id: number;
      account_type?: string;
      params: PaginationInput;
    }) =>
      HttpClient.get<IbanResponse>(
        API_ENDPOINTS.GET_BUISINESS_IBAN_ACCOUNTS(input.id, input.account_type),
        input.params,
      ),
    getReset2faList: (input: PaginationInput) =>
      HttpClient.get<IReset2FAResponse>(API_ENDPOINTS.GET_RESET2FA_LIST, input),
    getReset2faAccept: (input: { id: number }) =>
      HttpClient.put<any>(API_ENDPOINTS.GET_RESET2FA_LIST, input),
    getReset2fareject: (input: { id: number }) =>
      HttpClient.post<any>(API_ENDPOINTS.GET_RESET2FA_REJECT, input),
    getEmailHistoryList: (input: PaginationInput) =>
      HttpClient.get<IEmailHistoryResponse>(
        API_ENDPOINTS.GET_EMAIL_HISTORY_LIST,
        input,
      ),
    getSmsHistoryList: (input: PaginationInput) =>
      HttpClient.get<IEmailHistoryResponse>(
        API_ENDPOINTS.GET_SMS_HISTORY_LIST,
        input,
      ),
    riskLevelIndividual: (input: { risk_level: string; id: number }) =>
      HttpClient.put<any>(API_ENDPOINTS.PUT_RISK_LELVEL_INDIVIDUAL(input.id), {
        risk_level: input.risk_level,
      }),
    riskLevelBusiness: (input: { risk_level: string; id: number }) =>
      HttpClient.put<any>(API_ENDPOINTS.PUT_RISK_LELVEL_BUISINESS(input.id), {
        risk_level: input.risk_level,
      }),
  };
  bankAccount = {
    all: (input: PaginationInput) =>
      HttpClient.get<BankAccountsResponse>(
        API_ENDPOINTS.GET_BANK_ACCOUNTS,
        input,
      ),
    add: (input: BankAccountInput) =>
      HttpClient.post<BankAccount>(API_ENDPOINTS.GET_BANK_ACCOUNTS, input),
    update: (input: BankAccountUpdate) =>
      HttpClient.put<BankAccount>(API_ENDPOINTS.GET_BANK_ACCOUNTS, input),
    delete: (id: number) =>
      HttpClient.delete<StatusResponse>(
        `${API_ENDPOINTS.GET_BANK_ACCOUNTS}/${id}`,
      ),
  };
  transactions = {
    individualDeposit: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_INSIVIDUAL_DEPOSIT_TRANSACTIONS,
        input,
      ),
    downloadTransactionReceiptPdf: ({
      type,
      id,
      timezone,
    }: {
      type: string;
      id: number;
      timezone: string;
    }) =>
      HttpClient_1.getPdf(
        `${API_ENDPOINTS.TRANSACTION_RECEIPT_PDF
        }/${type}/${id}?timezone=${encodeURIComponent(timezone)}`,
      ),
    buisinessDeposit: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_BUISINESS_DEPOSIT_TRANSACTIONS,
        input,
      ),
    individualWithdrawal: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_WITHDRAWAL_TRANSACTIONS,
        input,
      ),
    buisinessWithdrawal: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_BUISINESS_WITHDRAWAL_TRANSACTIONS,
        input,
      ),
    transfer: (input: PaginationInput) =>
      HttpClient.get<TransfersResponse>(API_ENDPOINTS.GET_TRANSFERS, input),
    kwickpayIndividual: (input: PaginationInput) =>
      HttpClient.get<KwickpayHistoryResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_KWICKPAY_TRANSACTIONS,
        input,
      ),
    kwickpayBuisiness: (input: PaginationInput) =>
      HttpClient.get<KwickpayHistoryResponse>(
        API_ENDPOINTS.GET_BUISINESS_KWICKPAY_TRANSACTIONS,
        input,
      ),

    moneyMoversIndividual: (input: PaginationInput) =>
      HttpClient.get<MoneyMoversHistoryResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_MONEY_MOVERS_TRANSACTIONS,
        input,
      ),
    cashoutIndividual: (input: PaginationInput) =>
      HttpClient.get<MoneyMoversHistoryResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_MONEY_MOVERS_TRANSACTIONS,
        input,
      ),
    moneyMoversBuisiness: (input: PaginationInput) =>
      HttpClient.get<MoneyMoversHistoryResponse>(
        API_ENDPOINTS.GET_BUSINESS_MONEY_MOVERS_TRANSACTIONS,
        input,
      ),
    guestUserTransactions: (input: PaginationInput) =>
      HttpClient_1.get<PreLoginTransactionResponse>(
        API_ENDPOINTS.GET_GUEST_USER_TRANSACTION,
        input,
      ),
  };
  categories = {
    all: () =>
      HttpClient.get<BillingCategoriesResponse>(
        API_ENDPOINTS.GET_BILLING_CATEGORIES,
      ),
  };
  services = {
    all: () => HttpClient.get<string[]>(API_ENDPOINTS.GET_BILLING_SERVICES),
  };
  google2FA = {
    getStatus: () =>
      HttpClient_PermissionsModule.get<TwoFAStatusResponse>(
        API_ENDPOINTS.TWO_FA_STATUS,
      ),
    generateKey: () =>
      HttpClient.get<Google2FASecretKeyResponse>(
        API_ENDPOINTS.GOOGLE_2FA_GENERATE_KEY,
      ),
    enable: (input: { totp: string }) =>
      HttpClient.post<StatusResponse>(API_ENDPOINTS.GOOGLE_2FA_ENABLE, input),
    disable: (input: { totp: string }) =>
      HttpClient.post<StatusResponse>(API_ENDPOINTS.GOOGLE_2FA_DISABLE, input),
  };
  notification = {
    all: () => HttpClient.get<Notification[]>(API_ENDPOINTS.GET_NOTIFICATIONS),
    update: (input: NotificationInput) =>
      HttpClient.put<Notification>(API_ENDPOINTS.UPDATE_NOTIFICATION, input),
  };
  adminAlerts = {
    listAllAlerts: () =>
      HttpClient_2.get<any>(API_ENDPOINTS.GET_ALL_ALERT_NOTIFICATIONS),
    updateAlert: (params: any) =>
      HttpClient_2.put<any>(
        API_ENDPOINTS.UPDATE_ALERT_NOTIFICATIONS(params.id),
        params,
      ),
  };
  emailTemplate = {
    getAllTemplates: (input: newPaginationInput) =>
      HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_EMAIL_TEMPLATES,
        input,
      ),
    updateTemplate: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_EMAIL_TEMPLATE(id),
        payload,
      );
    },
    addTemplate: (payload: any) => {
      return HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_EMAIL_TEMPLATE,
        payload,
      );
    },
  };
  admins = {
    all: (input: PaginationInput) =>
      HttpClient.get<AdminUsersResponse>(API_ENDPOINTS.GET_ADMINS, input),
    add: (input: AdminUserInput) =>
      HttpClient.post<AdminUserResponse>(API_ENDPOINTS.GET_ADMINS, input),
    update: (input: { id: number; params: AdminUserUpdate }) =>
      HttpClient.put<AdminUser>(
        `${API_ENDPOINTS.GET_ADMIN}/${input.id}`,
        input.params,
      ),
    delete: (id: number) =>
      HttpClient.delete<StatusResponse>(`${API_ENDPOINTS.GET_ADMIN}/${id}`),
    permissions: () =>
      HttpClient.get<Permission[]>(API_ENDPOINTS.GET_PERMISSIONS),
    addPermissions: (input: AddPermissionInput) =>
      HttpClient_1.post<any>(API_ENDPOINTS.ADD_PERMISSIONS, input),
  };
  moneyMover = {
    categoriesList: () =>
      HttpClient.get<moneyMoversCategoriesResponse>(
        API_ENDPOINTS.GET_CATEGORIES_LIST,
      ),
    addCategory: (input: FormData) =>
      HttpClient.post<any>(API_ENDPOINTS.ADD_CATEGORY, input, {
        headers: {
          "Content-Type": "multipart/form-data; boundary=ExampleBoundaryString",
        },
      }),
    updateCategory: (input: updateCategoryInput) =>
      HttpClient.post<any>(
        `${API_ENDPOINTS.UPDATE_CATEGORY}/${input.id}`,
        input.payload,
        {
          headers: {
            "Content-Type":
              "multipart/form-data; boundary=ExampleBoundaryString",
          },
        },
      ),
    deleteCategory: (id: number) =>
      HttpClient.delete<any>(`${API_ENDPOINTS.DELETE_CATEGORY}/${id}`),
    fetchCategory: (id: number) =>
      HttpClient.get<moneyMoversCategory>(
        `${API_ENDPOINTS.GET_CATEGORIEY_DETAILS}/${id}`,
      ),
    servicesList: (input: PaginationInput) =>
      HttpClient.get<moneyMoversServiceResponse>(
        API_ENDPOINTS.GET_SERVICES_LIST,
        input,
      ),
    addService: (input: FormData) =>
      HttpClient.post<any>(API_ENDPOINTS.ADD_SERVICE, input, {
        headers: {
          "Content-Type": "multipart/form-data; boundary=ExampleBoundaryString",
        },
      }),
    fetchsService: (id: number) =>
      HttpClient.get<casinoServiceList>(`${API_ENDPOINTS.GET_SERVICE}/${id}`),

    updateService: (input: updateServiceInput) =>
      HttpClient.post<any>(
        `${API_ENDPOINTS.UPDATE_SERVICE}/${input.id}`,
        input.payload,
        {
          headers: {
            "Content-Type":
              "multipart/form-data; boundary=ExampleBoundaryString",
          },
        },
      ),
    deleteService: (id: number) =>
      HttpClient.delete<any>(`${API_ENDPOINTS.DELETE_SERVICE}/${id}`),
    updateServiceCategory: ({
      serviceId,
      categoryId,
    }: {
      serviceId: number;
      categoryId: number;
    }) =>
      HttpClient.patch<any>(
        `${API_ENDPOINTS.UPDATE_SERVICE_CATEGORY}/${serviceId}/${categoryId}`,
      ),

    cashoutServicesList: (input: PaginationInput) =>
      HttpClient.get<moneyMoversCashoutServiceResponse>(
        API_ENDPOINTS.GET_CASHOUT_SERVICES_LIST,
        input,
      ),
    addCashoutService: (input: FormData) =>
      HttpClient.post<any>(API_ENDPOINTS.ADD_CASHOUT_SERVICE, input, {
        headers: {
          "Content-Type": "multipart/form-data; boundary=ExampleBoundaryString",
        },
      }),
    fetchsCashoutService: (id: number) =>
      HttpClient.get<casinoServiceList>(
        `${API_ENDPOINTS.GET_CASHOUT_SERVICE_DETAILS}/${id}`,
      ),

    updateCashoutService: (input: updateServiceInput) =>
      HttpClient.post<any>(
        `${API_ENDPOINTS.UPDATE_CASHOUT_SERVICE}/${input.id}`,
        input.payload,
        {
          headers: {
            "Content-Type":
              "multipart/form-data; boundary=ExampleBoundaryString",
          },
        },
      ),
    deleteCashoutService: (id: number) =>
      HttpClient.delete<any>(`${API_ENDPOINTS.DELETE_CASHOUT_SERVICE}/${id}`),
  };

  currencies = {
    all: () => HttpClient.get<CurrencyResponse[]>(API_ENDPOINTS.GET_CURRENCIES),
    update: (input: CurrencyInput) =>
      HttpClient.put<CurrencyResponse>(API_ENDPOINTS.GET_CURRENCIES, input),
  };
  limitationsBusiness = {
    all: () =>
      HttpClient.get<Limitation[]>(
        API_ENDPOINTS.GET_SINGLE_BUSINESS_LIMITATIONS,
      ),
    update: (input: LimitationUpdate) =>
      HttpClient.put<Limitation>(
        API_ENDPOINTS.GET_SINGLE_BUSINESS_LIMITATIONS,
        input,
      ),
    export: () => HttpClient.get(API_ENDPOINTS.GET_SINGLE_BUSINESS_EXPORT),
  };
  dailyLimitsBusiness = {
    all: () =>
      HttpClient.get<DailyLimit[]>(
        API_ENDPOINTS.GET_DAILY_BUSINESS_LIMITATIONS,
      ),
    update: (input: DailyLimitUpdate) =>
      HttpClient.put<DailyLimit>(
        API_ENDPOINTS.GET_DAILY_BUSINESS_LIMITATIONS,
        input,
      ),
    export: () => HttpClient.get(API_ENDPOINTS.GET_DAILY_BUSINESS_EXPORT),
  };
  limitations = {
    all: () =>
      HttpClient.get<Limitation[]>(API_ENDPOINTS.GET_SINGLE_LIMITATIONS),
    update: (input: LimitationUpdate) =>
      HttpClient.put<Limitation>(API_ENDPOINTS.GET_SINGLE_LIMITATIONS, input),
    export: () => HttpClient.get(API_ENDPOINTS.GET_SINGLE_EXPORT),
  };
  dailyLimits = {
    all: () =>
      HttpClient.get<DailyLimit[]>(API_ENDPOINTS.GET_DAILY_LIMITATIONS),
    update: (input: DailyLimitUpdate) =>
      HttpClient.put<DailyLimit>(API_ENDPOINTS.GET_DAILY_LIMITATIONS, input),
    export: () => HttpClient.get(API_ENDPOINTS.GET_DAILY_EXPORT),
  };

  billingsLimitations = {
    all: ({ userType, type, ...params }: ServiceParamsInput) =>
      HttpClient.get<LimitationResponse>(
        `${API_ENDPOINTS.GET_SINGLE_BILLING_LIMITATIONS}/${userType}/${type}`,
        params,
      ),
    update: ({ userType, type, ...input }: BillingLimitationUpdate) =>
      HttpClient.put<Limitation>(
        `${API_ENDPOINTS.GET_SINGLE_BILLING_LIMITATIONS}/${userType}/${type}`,
        input,
      ),
  };

  billingsDailyLimits = {
    all: ({ userType, type, ...params }: ServiceParamsInput) =>
      HttpClient.get<DailyLimitResponse>(
        `${API_ENDPOINTS.GET_SINGLE_BILLING_LIMITATIONS}/${userType}/${type}`,
        params,
      ),
    update: (input: DailyLimitUpdate) =>
      HttpClient.put<DailyLimit>(API_ENDPOINTS.GET_DAILY_LIMITATIONS, input),
    export: () => HttpClient.get(API_ENDPOINTS.GET_DAILY_EXPORT),
  };

  fee = {
    all: (input: ParamsSort) =>
      HttpClient.get<Fee[]>(API_ENDPOINTS.GET_FEES_INDIVIDUAL, input),
    update: (input: FeeUpdate) => {
      return HttpClient.put<Fee>(
        API_ENDPOINTS.GET_FEES_INDIVIDUAL_PERCENT,
        input,
      );
    },
    updateRate: (input: FeeUpdate) => {
      return HttpClient.put<Fee>(API_ENDPOINTS.GET_FEES_INDIVIDUAL, input);
    },
    add: (input: ParamsSort) =>
      HttpClient.post<Fee>(API_ENDPOINTS.GET_FEES_INDIVIDUAL, input),
    delete: (id: number) =>
      HttpClient.delete<Fee>(`${API_ENDPOINTS.GET_FEES_INDIVIDUAL}/${id}`),
    export: () => HttpClient.get(API_ENDPOINTS.GET_FEES_INDIVIDUAL_EXPORT),
    listFee: () => HttpClient.get<string[]>(API_ENDPOINTS.GET_FIAT_LIST),
    listChanels: () => HttpClient.get<string[]>(API_ENDPOINTS.GET_CHANELS),
  };
  feeBiling = {
    all: ({ type, ...params }: ServiceParamsInput) =>
      HttpClient.get<Fee[]>(
        `${API_ENDPOINTS.GET_BILLING_FEES}/${type}`,
        params,
      ),
  };
  feePreLogin = {
    all: (input: ParamsSort_1) =>
      HttpClient_1.get<FeeResponse>(
        `${API_ENDPOINTS.GET_PRE_LOGIN_FEES}`,
        input,
      ),
    update: (input: FeeUpdate) => {
      return HttpClient_1.put<Fee>(API_ENDPOINTS.GET_PRE_LOGIN_FEES, input);
    },
    add: (input: ParamsSort) =>
      HttpClient_1.post<Fee>(API_ENDPOINTS.GET_PRE_LOGIN_FEES, input),
    remove: (id: number) =>
      HttpClient_1.delete<Fee>(`${API_ENDPOINTS.GET_PRE_LOGIN_FEES}/${id}`),
    export: () => HttpClient_1.get(API_ENDPOINTS.GET_PRE_LOGIN_FEES),
  };
  feeBusiness = {
    all: (input: ParamsSort) =>
      HttpClient.get<Fee[]>(API_ENDPOINTS.GET_FEES_BUSINESS, input),
    update: (input: FeeUpdate) =>
      HttpClient.put<Fee>(API_ENDPOINTS.GET_FEES_BUSINESS_PERCENT, input),
    updateRate: (input: FeeUpdate) =>
      HttpClient.put<Fee>(API_ENDPOINTS.GET_FEES_BUSINESS, input),
    add: (input: ParamsSort) =>
      HttpClient.post<Fee>(API_ENDPOINTS.GET_FEES_BUSINESS, input),
    delete: (id: number) =>
      HttpClient.delete<Fee>(`${API_ENDPOINTS.GET_FEES_BUSINESS}/${id}`),
    export: () => HttpClient.get(API_ENDPOINTS.GET_FEES_BUSINESS_EXPORT),
    listFee: () => HttpClient.get<string[]>(API_ENDPOINTS.GET_FIAT_LIST),
    listChanels: () => HttpClient.get<string[]>(API_ENDPOINTS.GET_CHANELS),
  };
  pendingDeposits = {
    individual: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_PENDING_DEPOSIT,
        input,
      ),
    confirmIndividual: (input: { id: number; amount: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CONFIRM_INDIVIDUAL_PENDING_DEPOSIT,
        input,
      ),
    cancelIndividual: (input: { id: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CANCEL_INDIVIDUAL_PENDING_DEPOSIT,
        input,
      ),
    buisiness: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_BUSINESS_PENDING_DEPOSIT,
        input,
      ),
    confirmBuisiness: (input: { id: number; amount: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CONFIRM_BUSINESS_PENDING_DEPOSIT,
        input,
      ),
    cancelBuisiness: (input: { id: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CANCEL_BUSINESS_PENDING_DEPOSIT,
        input,
      ),
  };
  manualTransfers = {
    // individualPending: (input: any) =>
    //   HttpClient_1.get<any>(API_ENDPOINTS.GET_MANUAL_INDIVIDUAL_PENDING, input),
    individualPending: (input: any) => {
      const headerObj = generateHmac(
        input,
        "GET",
        `/api/v1${API_ENDPOINTS.GET_MANUAL_INDIVIDUAL_PENDING}`,
      );
      return ManualTransactionsHttpClient.get<any>(
        `${API_ENDPOINTS.GET_MANUAL_INDIVIDUAL_PENDING}`,
        input,
        { ...headerObj },
      );
    },

    // businessPending: (input: any) =>
    //   HttpClient_1.get<any>(API_ENDPOINTS.GET_MANUAL_BUSINESS_PENDING, input),
    businessPending: (input: any) => {
      const headerObj = generateHmac(
        input,
        "GET",
        `/api/v1${API_ENDPOINTS.GET_MANUAL_BUSINESS_PENDING}`,
      );
      return ManualTransactionsHttpClient.get<any>(
        `${API_ENDPOINTS.GET_MANUAL_BUSINESS_PENDING}`,
        input,
        { ...headerObj },
      );
    },

    // getCurrencies: () =>
    //   HttpClient_1.get<any>(API_ENDPOINTS.GET_MANUAL_TRANSFER_CURRENCIES),
    getCurrencies: () => {
      const headerObj = generateHmac(
        "",
        "GET",
        `/api/v1${API_ENDPOINTS.GET_MANUAL_TRANSFER_CURRENCIES}`,
      );
      return ManualTransactionsHttpClient.get<any>(
        `${API_ENDPOINTS.GET_MANUAL_TRANSFER_CURRENCIES}`,
        {},
        { ...headerObj },
      );
    },
    // verifyUser: (input: { searchBy: string; searchValue: string }) =>
    //   HttpClient_1.post(API_ENDPOINTS.FIND_USER_FOR_MANUAL_TRANSACTION, input),
    verifyUser: (input: { searchBy: string; searchValue: string }) => {
      const headerObj = generateHmac(
        input,
        "POST",
        `/api/v1${API_ENDPOINTS.FIND_USER_FOR_MANUAL_TRANSACTION}`,
      );
      return ManualTransactionsHttpClient.post<any>(
        `${API_ENDPOINTS.FIND_USER_FOR_MANUAL_TRANSACTION}`,
        input,
        {
          headers: {
            ...headerObj,
          },
        },
      );
    },
    // createManualTransaction: (input: {
    //   user_id: number;
    //   type: string;
    //   amount: number;
    //   comment: string;
    //   asset_id: number;
    //   is_comment_visible: boolean;
    //   otp: string;
    // }) => HttpClient_1.post(API_ENDPOINTS.CREATE_MAUAL_TRANSFER, input),

    createManualTransaction: (input: {
      user_id: number;
      type: string;
      amount: number;
      comment: string;
      asset_id: number;
      is_comment_visible: boolean;
      otp: string;
    }) => {
      const headerObj = generateHmac(
        input,
        "POST",
        `/api/v1${API_ENDPOINTS.CREATE_MAUAL_TRANSFER}`,
      );
      return ManualTransactionsHttpClient.post<any>(
        `${API_ENDPOINTS.CREATE_MAUAL_TRANSFER}`,
        input,
        {
          headers: {
            ...headerObj,
          },
        },
      );
    },
    // confirmTransfer: (input: { id: number; otp: string }) =>
    //   HttpClient_1.post<StatusResponse>(
    //     API_ENDPOINTS.APPROVE_MANUAL_TRANSFER,
    //     input
    //   ),

    confirmTransfer: (input: { id: number; otp: string }) => {
      const headerObj = generateHmac(
        input,
        "POST",
        `/api/v1${API_ENDPOINTS.APPROVE_MANUAL_TRANSFER}`,
      );
      return ManualTransactionsHttpClient.post<StatusResponse>(
        `${API_ENDPOINTS.APPROVE_MANUAL_TRANSFER}`,
        input,
        {
          headers: {
            ...headerObj,
          },
        },
      );
    },
    // cancelTransfer: (input: { id: number; otp: string; comment: string }) =>
    //   HttpClient_1.post<StatusResponse>(
    //     API_ENDPOINTS.CANCEL_MANUAL_TRANSFER,
    //     input
    //   ),
    cancelTransfer: (input: { id: number; otp: string; comment: string }) => {
      const headerObj = generateHmac(
        input,
        "POST",
        `/api/v1${API_ENDPOINTS.CANCEL_MANUAL_TRANSFER}`,
      );
      return ManualTransactionsHttpClient.post<StatusResponse>(
        `${API_ENDPOINTS.CANCEL_MANUAL_TRANSFER}`,
        input,
        {
          headers: {
            ...headerObj,
          },
        },
      );
    },
  };
  unidentifiedDeposits = {
    all: (input: PaginationInput) =>
      HttpClient.get<UnidentifyDepositResponse>(
        API_ENDPOINTS.GET_UNIDENTIFIED_DEPOSITS,
        input,
      ),
    confirm: (input: { id: number; user_id: number }) =>
      HttpClient.put<StatusResponse>(
        API_ENDPOINTS.CONFIRM_UNIDENTIFIED_DEPOSIT,
        input,
      ),
    reject: (input: { id: number; comment: string }) =>
      HttpClient.put<StatusResponse>(
        API_ENDPOINTS.REJECT_UNIDENTIFIED_DEPOSIT,
        input,
      ),
    findUser: (input: { search_field?: string; search_value?: string }) =>
      HttpClient.get<User[]>(API_ENDPOINTS.FIND_USER, input),
  };
  pendingWithdrawals = {
    individual: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_PENDING_WITHDRAWAL,
        input,
      ),
    confirmIndividual: (input: { id: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CONFIRM_INDIVIDUAL_PENDING_WITHDRAWAL,
        input,
      ),
    cancelIndividual: (input: { id: number; comment: string }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CANCEL_INDIVIDUAL_PENDING_WITHDRAWAL,
        input,
      ),
    buisiness: (input: PaginationInput) =>
      HttpClient.get<TransactionsResponse>(
        API_ENDPOINTS.GET_BUSINESS_PENDING_WITHDRAWAL,
        input,
      ),
    confirmBuisiness: (input: { id: number }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CONFIRM_BUSINESS_PENDING_WITHDRAWAL,
        input,
      ),
    cancelBuisiness: (input: { id: number; comment: string }) =>
      HttpClient.post<StatusResponse>(
        API_ENDPOINTS.CANCEL_BUSINESS_PENDING_WITHDRAWAL,
        input,
      ),
  };
  ourPrices = {
    all: (input: IGetOurPricesInput) =>
      HttpClient.get<TOurPricesResponse>(API_ENDPOINTS.OUR_PRICES, input),
    add: (input: ICreateOurPricesInput) =>
      HttpClient.post<IOurPricesItem>(API_ENDPOINTS.OUR_PRICES, input),
    update: (input: IUpdateOurPricesInput) =>
      HttpClient.put<IOurPricesItem>(API_ENDPOINTS.OUR_PRICES, input),
    delete: (id: number) =>
      HttpClient.delete<StatusResponse>(`${API_ENDPOINTS.OUR_PRICES}/${id}`),
  };
  individualUserSettings = {
    all: (id: number) =>
      HttpClient.get<IuserSettingsResponse>(
        API_ENDPOINTS.GET_INDIVIDUAL_USET_SETTINGS(id),
      ),
  };
  busienessUserSettings = {
    all: (id: number) =>
      HttpClient.get<IuserSettingsResponse>(
        API_ENDPOINTS.GET_BUISINESS_USET_SETTINGS(id),
      ),
  };
  mts = {
    list: (input: any) =>
      HttpClient.get<IMTSPaymentTransactionResponse>(
        API_ENDPOINTS.GET_MTS_LIST,
        input,
      ),
  };
  translations = {
    allLanguages: () =>
      HttpClient.get<LanguagesList>(API_ENDPOINTS.GET_LANGUAGES),
    allModules: (input: { language: string; platform: string }) =>
      HttpClient.get<IModuleData>(
        `${API_ENDPOINTS.GET_ALL_MODULES}?language_code=${input.language}&platform=${input.platform}`,
      ),
    fetchModuleDetails: (input: IModuleInput) =>
      HttpClient.get<ITranslations>(
        `${API_ENDPOINTS.GET_MODULE_DETAILS}`,
        input,
      ),
    updateModuleDetails: (input: IUpdateModuleInput) =>
      HttpClient.put<ITranslations>(
        `${API_ENDPOINTS.UPDATE_MODULE_TRANSLATION}`,
        input,
      ),
    globalSearch: (input: any) =>
      HttpClient.get<ITranslations>(
        `${API_ENDPOINTS.GLOBAL_SEARCH_TRANSLATION}`,
        input,
      ),
    uploadTranslations: (input: any) =>
      HttpClient_1.post<any>(`${API_ENDPOINTS.ADD_TRANSLATION_FILE}`, input),
  };

  walletToWallet = {
    getWalletIndividualTransactionsList: (input: any) =>
      HttpClient_1.get<ConversionResponse>(
        API_ENDPOINTS.GET_WTW_INDIVIDUAL_TRANSACTIONS_LIST,
        input,
      ),
    getWalletBusinessTransactionsList: (input: any) =>
      HttpClient_1.get<ConversionResponse>(
        API_ENDPOINTS.GET_WTW_BUSINESS_TRANSACTIONS_LIST,
        input,
      ),
    getIndividualUserW2WTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient_1.get<any>(
        API_ENDPOINTS.GET_INDIVIDUAL_USER_W2W_TRANSACTIONS(input.id),
        input.params,
      ),
    getBusinessUserW2WTransactions: (input: {
      id: number;
      params: PaginationInput;
    }) =>
      HttpClient_1.get<any>(
        API_ENDPOINTS.GET_BUSINESS_USER_W2W_TRANSACTIONS(input.id),
        input.params,
      ),
    exportTransactionList: ({ type, ...params }: any) =>
      HttpClient.get<any>(
        `${API_ENDPOINTS.EXPORT_WALLET_TRANSACTION_LIST(String(type))}`,
        params,
      ),
  };

  walletTerminalTransactions = {
    getWalletTerminalTransactionsList: (input: any) =>
      HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_WALLET_TERMINAL_TRANSACTIONS_LIST,
        input,
      ),

    exportTransactionList: ({ type, ...params }: any) =>
      HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.EXPORT_WALLET_TERMINAL_TRANSACTION_LIST}`,
        params,
      ),
    viewHistoryLogs: (input: any) =>
      HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.VIEW_WALLET_TERMINAL_HISTORY_LOGS}`,
        input,
      ),
  };
  conversions = {
    all: () =>
      HttpClient_AdminServiceV2.get<ConversionResponseV2>(
        API_ENDPOINTS.GET_CONVERSIONS_LIST,
        // input,
      ),
    update: (input: ConversionUpdate) => {
      const { id, ...payload } = input;

      return HttpClient_AdminServiceV2.put(
        `${API_ENDPOINTS.GET_CONVERSIONS_LIST}/${id}`,
        payload,
      );
    },
    getOrderList: (input: any) => {
      const { type, ...params } = input;
      return HttpClient_1.get<ConversionListApiResponse>(
        `${API_ENDPOINTS.GET_CONVERSION_ORDER_LIST(String(type))}`,
        params,
      );
    },

    userGetOrderList: (input: any) => {
      const { type, id, params } = input;
      return HttpClient_1.get<ConversionListApiResponse>(
        `${API_ENDPOINTS.GET_USER_CONVERSION_ORDER_LIST(String(type))}/${id}`,
        params,
      );
    },
    exportOrderList: ({ type, ...params }: any) =>
      HttpClient.get<any>(
        `${API_ENDPOINTS.EXPORT_USER_CONVERSION_ORDER_LIST(String(type))}`,
        params,
      ),
  };

  selfTerminal = {
    all: (input: any) =>
      HttpClient_1.get<ProviderListResponse>(
        API_ENDPOINTS.GET_PROVIDER_LIST,
        input,
      ),
    create: (input: any) =>
      HttpClient_1.post<any>(API_ENDPOINTS.CREATE_PROVIDER, input),
    update: (input: any) =>
      HttpClient_1.put<any>(
        API_ENDPOINTS.UPDATE_PROVIDER(Number(input.id)),
        input.row,
      ),
    remove: (input: any) =>
      HttpClient_1.delete<any>(API_ENDPOINTS.REMOVE_PROVIDER(Number(input.id))),

    getAllTransactions: (input: SelfTerminalPaginationInput) =>
      HttpClient_1.get<any>(API_ENDPOINTS.GET_PROVIDER_TRANSACTIONS, input),
  };

  walletTerminal = {
    all: (input: any) =>
      HttpClient_EmailTemplate.get<any>(API_ENDPOINTS.WALLET_TERMINALS, input),
    create: (input: any) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.WALLET_TERMINALS, input),
    update: (input: any) => {
      const { id, ...payload } = input;

      return HttpClient_EmailTemplate.put<any>(
        `${API_ENDPOINTS.WALLET_TERMINALS}/${id}`,
        payload,
      );
    },
    remove: (input: any) =>
      HttpClient_EmailTemplate.delete<any>(
        `${API_ENDPOINTS.WALLET_TERMINALS}/${input.id}`,
      ),
  };

  admin = {
    all: (input: PaginationInput) =>
      HttpClient.get<any>(API_ENDPOINTS.GET_ADMIN_ACTIVITY_LIST, input),
    logs: (input: any) => {
      const { per_page, current_page, page, limit, ...rest } = input || {};
      const params: any = {
        limit: limit ?? per_page,
        page: page ?? current_page,
        ...rest,
      };
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ADMIN_ACTIVITY_LOGS,
        params
      );
    },
  };

  feeMgt = {
    getList: (input: any) =>
      HttpClient_EmailTemplate.get<any>(API_ENDPOINTS.GET_FEES_LIST, input),
    getChannelList: () =>
      HttpClient_EmailTemplate.get<any>(API_ENDPOINTS.GET_CHANNEL_LIST),

    // create: (input: any) =>
    //   HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.WALLET_TERMINALS, input),
    update: (input: any) => {
      const { id, ...payload } = input;

      return HttpClient_EmailTemplate.put<any>(
        `${API_ENDPOINTS.UPDATE_FEES}/${id}`,
        payload,
      );
    },
    // remove: (input: any) =>
    //   HttpClient_EmailTemplate.delete<any>(
    //     `${API_ENDPOINTS.WALLET_TERMINALS}/${input.id}`
    //   ),
  };

  newLimit = {
    add: (input: ParamsSort) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.ADD_LIMITS_NEW, input),
    addDaily: (input: ParamsSort) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_DAILY_LIMITS_NEW,
        input,
      ),
  };

  serviceMaintenance = {
    getAllServicesList: (input: newPaginationInput) =>
      HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_SERVICES_LIST,
        input,
      ),
    updateService: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_SERVICES(id),
        payload,
      );
    },
    getGloabalMaintenance: () =>
      HttpClient_EmailTemplate.get<any>(API_ENDPOINTS.GLOBAL_MAINTENANCE),
    updateGlobalMaintenance: ({ id, ...params }: any) => {
      return HttpClient_EmailTemplate.put<any>(
        `${API_ENDPOINTS.GLOBAL_MAINTENANCE}/${id}`,
        params,
      );
    },
  };
  serviceAlerts = {
    getAllServicesList: (input: newPaginationInput) =>
      HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_SERVICES_ALERTS_LIST,
        input,
      ),
    updateService: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_SERVICES_ALERTS(id),
        payload,
      );
    },
  };
  serviceIncidents = {
    getAllIncidentsList: (input: newPaginationInput) =>
      HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_SERVICES_INCIDENTS_LIST,
        input,
      ),
    updateIncidents: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_SERVICES_ALERTS(id),
        payload,
      );
    },
  };
  newUserKycInfo = {
    getKycInfoDocs: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_KYC_INFO_DOCS(id),
      );
    },
    getKycInfoImages: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_KYC_INFO_IMAGES(id),
        payload,
      );
    },
    getKycInfoEvents: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_KYC_INFO_EVENTS(id),
      );
    },
    getInfoConsent: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_INFO_CONSENT(id),
      );
    },
    getKycInfoQuestionnaire: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_KYC_INFO_QUESTIONNAIRE(id),
      );
    },
  };
  cardPackages = {
    list: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_PACKAGES_LIST,
        params,
      );
    },
    view: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.VIEW_PARTICULAR_PACKAGE(id),
      );
    },
    add: (input: ParamsSort) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_CARD_PACKAGES,
        input,
      ),
    update: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_CARD_PACKAGES(id),
        payload,
      );
    },
    delete: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.delete<any>(
        API_ENDPOINTS.DELETE_CARD_PACKAGES(id),
      );
    },
  };
  cardProducts = {
    list: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_PRODUCTS_LIST,
        params,
      );
    },
    view: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.VIEW_PARTICULAR_PRODUCT(id),
      );
    },
    add: (input: any) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.ADD_CARD_PRODUCT, input),
    update: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_CARD_PRODUCT(id),
        payload,
      );
    },
    delete: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.delete<any>(
        API_ENDPOINTS.DELETE_CARD_PRODUCT(id),
      );
    },
  };
  cardTermsConditions = {
    list: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_TERMS_CONDITIONS_LIST,
        params,
      );
    },
    view: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.VIEW_PARTICULAR_TERMS_CONDITIONS(id),
      );
    },
    add: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_CARD_TERMS_CONDITIONS,
        input,
      ),
    update: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_CARD_TERMS_CONDITIONS(id),
        payload,
      );
    },
    delete: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.delete<any>(
        API_ENDPOINTS.DELETE_CARD_TERMS_CONDITIONS(id),
      );
    },
  };
  cardMerchants = {
    allCardMerchantsList: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GET_CARD_MERCHANTS_LIST,
        input,
      ),
    viewParticularMerchant: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.VIEW_PARTICULAR_MERCHANT,
        input,
      ),

    addCardMerchant: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_CARD_MERCHANT,
        input,
      ),
    createCardMerchantApiKey: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.CREATE_MERCHANT_API_KEY,
        input,
      ),
    revokeCardMerchantApiKey: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.REVOKE_MERCHANT_API_KEY,
        input,
      ),
    addCardMerchantIpAddresses: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ADD_MERCHANT_IP_WHITELIST,
        input,
      ),
  };
  cardConversionRates = {
    getCurrencyConversionRateslist: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_CONVERSION_CURRENCY_RATES,
        params,
      );
    },
    syncConversionCurrencyRates: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.SYNC_CONVERSION_CURRENCY_RATES,
        params,
      );
    },

    getCurrencyConversionRateslistHistory: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GET_ALL_CONVERSION_CURRENCY_RATES_HISTORY,
        input,
      ),

    updateCardCurrencyMargin: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.UPDATE_CARD_CURRENCY_MARGIN,
        input,
      ),
  };
  cardTransactions = {
    getAllCardTransactions: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GET_ALL_CARD_TRANSACTIONS,
        input,
      ),
  };
  cardBillings = {
    list: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_BILLINGS_LIST,
        params,
      );
    },
  };
  cardTypes = {
    list: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_TYPES_LIST,
        params,
      );
    },
    view: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.VIEW_PARTICULAR_CARD_TYPE(id),
      );
    },
    add: (input: ParamsSort) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.ADD_CARD_TYPES, input),
    update: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.put<any>(
        API_ENDPOINTS.UPDATE_CARD_TYPES(id),
        payload,
      );
    },
    delete: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.delete<any>(
        API_ENDPOINTS.DELETE_CARD_TYPES(id),
      );
    },
  };
  userVirtualCards = {
    getList: (params: any) => {
      const { id, ...payload } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USERS_VIRTUAL_CARDS_LIST(id),
      );
    },
    blockCard: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.BLOCK_USER_VIRTUAL_CARD,
        input,
      ),
    unblockCard: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.UNBLOCK_USER_VIRTUAL_CARD,
        input,
      ),
    closeCard: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.CLOSE_USER_VIRTUAL_CARD,
        input,
      ),
    generatePin: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GENERATE_USER_VIRTUAL_CARD_PIN,
        input,
      ),
    enableGambling: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.ENABLE_GAMBLING_USER_VIRTUAL_CARD,
        input,
      ),
    disableGambling: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.DISABLE_GAMBLING_USER_VIRTUAL_CARD,
        input,
      ),
    getUserDetails: (params: any) => {
      const { id } = params;
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_USER_DETAILS_BY_CARD_ID(id),
      );
    },

    userCardBalanceSyncList: (input: any) =>
      HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.SYNC_USER_CARD_BALANCES_LIST}`,
        input,
      ),
    syncFastooToBank: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.SYNC_FASTOO_TO_BANK_BALANCE,
        input,
      ),
    syncBankToFastoo: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.SYNC_BANK_TO_FASTOO_WALLET_BALANCE,
        input,
      ),
  };
  cardPoolManagement = {
    getPoolSummary: () => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_CARD_POOL_SUMMARY,
      );
    },
    generateCardPool: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GENERATE_CARD_POOL,
        input,
      ),
  };
  cardManagementOtherApis = {
    getAllOrderedCardsList: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_ALL_ORDERED_CARDS,
        params,
      );
    },
    getKafkaNotificationsList: (input: any) =>
      HttpClient_EmailTemplate.post<any>(
        API_ENDPOINTS.GET_KAFKA_NOTIFICATIONS,
        input,
      ),
    //v1/users/sync/kafka-notifications

    getSyncKafkaHistoryList: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.GET_KAFKA_SYNC_HISTORY,
        params,
      );
    },
  };
  landingPageApis = {
    // FAQ's
    GetAllFaqsList: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.FAQ_MANAGEMENT,
        params,
      );
    },
    CreateNewFaq: (input: any) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.FAQ_MANAGEMENT, input),
    UpdateFaq: (input: any) => {
      const { id, ...payload } = input;
      return HttpClient_EmailTemplate.put<any>(
        `${API_ENDPOINTS.FAQ_MANAGEMENT}/${id}`,
        payload,
      );
    },
    DeleteFaq: (id: any) => {
      return HttpClient_EmailTemplate.delete<any>(
        `${API_ENDPOINTS.FAQ_MANAGEMENT}/${id}`,
      );
    },
    // Blog posts
    GetAllBlogsList: (params: any) => {
      return HttpClient_EmailTemplate.get<any>(
        API_ENDPOINTS.BLOG_MANAGEMENT,
        params,
      );
    },
    CreateNewBlog: (input: any) =>
      HttpClient_EmailTemplate.post<any>(API_ENDPOINTS.BLOG_MANAGEMENT, input),
    UpdateBlog: (input: any) => {
      const { id, ...payload } = input;
      return HttpClient_EmailTemplate.put<any>(
        `${API_ENDPOINTS.BLOG_MANAGEMENT}/${id}`,
        payload,
      );
    },
    DeleteBlog: (id: any) => {
      return HttpClient_EmailTemplate.delete<any>(
        `${API_ENDPOINTS.BLOG_MANAGEMENT}/${id}`,
      );
    },
    UploadImage: (input: FormData) =>
      HttpClient_EmailTemplate.post(API_ENDPOINTS.UPLOAD_IMAGE, input, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    UploadImageWhiteLabel: (input: FormData) =>
      HttpClient_EmailTemplate.post(API_ENDPOINTS.UPLOAD_IMAGE_WHITE_LABEL, input, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),

  };
  whiteLabelApis = {
    UploadImageWhiteLabel: (input: FormData) =>
      HttpClient_PermissionsModule.post(API_ENDPOINTS.UPLOAD_IMAGE_WHITE_LABEL, input, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
  };
  newTransactionsHistory = {
    AllPaymentsHistory: (input: any) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.PAYMENTS_TRANSACTIONS_HISTORY}`,
        input,
      );
    },
    AllPaymentsHistoryDropdowns: ({ user_type }: any) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.PAYMENTS_TRANSACTIONS_HISTORY_DROPDOWNS}`,
        { user_type },
      );
    },
    //PAYMENTS_TRANSACTIONS_HISTORY_DROPDOWNS
    ExportAllPaymentsHistoryPdf: (input: any) => {
      return HttpClient_EmailTemplate.post<any>(
        `${API_ENDPOINTS.PAYMENTS_TRANSACTIONS_EXPORT_PDF}`,
        input,
      );
    },
    ExportAllPaymentsHistoryExcel: (input: any) => {
      return HttpClient_EmailTemplate.post<any>(
        `${API_ENDPOINTS.PAYMENTS_TRANSACTIONS_EXPORT_EXCEL}`,
        input,
      );
    },
    ExportHistoryList: () => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.EXPORTS_HISTORY_LIST}`,
      );
    },
    DownloadFromExportHistoryList: (id: number) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.DOWNLOAD_EXPORT_LIST}/${id}`,
      );
    },
    downloadTransactionReceiptPdf: ({
      receipt_type,
      id,
      timezone,
    }: {
      receipt_type: string;
      id: number;
      timezone: string;
    }) =>
      HttpClient_1.getPdf(
        `${API_ENDPOINTS.TRANSACTION_RECEIPT_PDF
        }/${receipt_type}/${id}?timezone=${encodeURIComponent(timezone)}`,
      ),
    //GET_MTS_TRANSFER_LOGS
    getMtsTransferLogs: (input: any) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.GET_MTS_TRANSFER_LOGS}`,
        input,
      );
    },
  };
  permissions = {
    getPermissions: async (
      page: number = 1,
      limit: number = 10,
      search_value?: string,
      search_field: string = "name"
    ) => {
      const params: any = { page, limit };
      if (search_value && search_value.trim() !== "") {
        params.search_value = search_value.trim();
        params.search_field = search_field;
      }
      const resData = await HttpClient_PermissionsModule.get<any>(API_ENDPOINTS.PERMISSIONS, params);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch permissions"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: any[] = [];
      if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData?.permissions)) {
        list = resData.permissions;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      const total = Number(resData?.totalcount) || Number(resData?.total) || list.length;
      return { data: list, total };
    },
    getPermissionById: async (id: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(`${API_ENDPOINTS.PERMISSIONS}/${id}`);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch permission details"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData?.data || resData;
    },
    createPermission: (payload: string | { name: string; description?: string }) => {
      const body = typeof payload === "string" ? { name: payload } : payload;
      return HttpClient_PermissionsModule.post<any>(API_ENDPOINTS.PERMISSIONS, body);
    },
    updatePermission: (id: number | string, payload: string | { name?: string; description?: string }) => {
      const body = typeof payload === "string" ? { name: payload } : payload;
      return HttpClient_PermissionsModule.put<any>(`${API_ENDPOINTS.PERMISSIONS}/${id}`, body);
    },
    deletePermission: (id: number | string) =>
      HttpClient_PermissionsModule.delete<any>(`${API_ENDPOINTS.PERMISSIONS}/${id}`),
    getRoles: async (
      page: number = 1,
      limit: number = 10,
      search_value?: string,
      search_field: string = "name"
    ) => {
      const params: any = { page, limit };
      if (search_value && search_value.trim() !== "") {
        params.search_value = search_value.trim();
        params.search_field = search_field;
      }
      const resData = await HttpClient_PermissionsModule.get<any>(API_ENDPOINTS.PERMISSIONS_ROLES, params);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch roles"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: any[] = [];
      if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      const total = Number(resData?.totalcount) || Number(resData?.total) || list.length;
      return { data: list, total };
    },
    getActiveChannelPartners: async () => {
      const resData = await HttpClient_PermissionsModule.get<any>(API_ENDPOINTS.CHANNEL_PARTNER_ACTIVE);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch active channel partners"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: ActiveChannelPartnerItem[] = [];
      if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      return list;
    },
    getChannelPartners: async (
      page: number = 1,
      limit: number = 10,
      search_value?: string,
      search_field: string = "name"
    ) => {
      const params: any = { page, limit };
      if (search_value && search_value.trim() !== "") {
        params.search_value = search_value.trim();
        params.search_field = search_field;
      }
      const resData = await HttpClient_PermissionsModule.get<any>(API_ENDPOINTS.CHANNEL_PARTNER, params);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch channel partners"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: ChannelPartnerApiItem[] = [];
      if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      const total = Number(resData?.totalcount) || Number(resData?.total) || list.length;
      return { data: list, total };
    },
    getChannelPartnerById: async (id: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(`${API_ENDPOINTS.CHANNEL_PARTNER}/${id}`);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch channel partner details"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData?.data || resData;
    },
    createChannelPartner: (payload: CreateChannelPartnerInput) =>
      HttpClient_PermissionsModule.post<any>(API_ENDPOINTS.CHANNEL_PARTNER, payload),
    updateChannelPartner: (id: number | string, payload: UpdateChannelPartnerInput) =>
      HttpClient_PermissionsModule.put<any>(`${API_ENDPOINTS.CHANNEL_PARTNER}/${id}`, payload),
    deleteChannelPartner: (id: number | string) =>
      HttpClient_PermissionsModule.delete<any>(`${API_ENDPOINTS.CHANNEL_PARTNER}/${id}`),
    getActiveRoles: async (channel_partner_id?: number | string) => {
      const params: any = {};
      if (
        channel_partner_id !== undefined &&
        channel_partner_id !== null &&
        channel_partner_id !== ""
      ) {
        params.channel_partner_id = channel_partner_id;
      }
      const resData = await HttpClient_PermissionsModule.get<any>(
        API_ENDPOINTS.PERMISSIONS_ACTIVE_ROLES,
        params
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch active roles"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: RoleApiItem[] = [];
      if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData?.roles)) {
        list = resData.roles;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      return list;
    },
    getRoleById: async (id: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(`${API_ENDPOINTS.PERMISSIONS_ROLES}/${id}`);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch role details"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData?.data || resData;
    },
    createRole: (payload: CreateRoleInput | { name: string; channel_partner_id?: number | null } | string) => {
      const body = typeof payload === "string" ? { name: payload } : payload;
      return HttpClient_PermissionsModule.post<any>(API_ENDPOINTS.PERMISSIONS_ROLES, body);
    },
    duplicateRole: (payload: DuplicateRoleInput) =>
      HttpClient_PermissionsModule.post<any>(API_ENDPOINTS.PERMISSIONS_DUPLICATE_ROLE, payload),
    updateRole: (
      id: number | string,
      payload: string | UpdateRoleInput
    ) => {
      const body = typeof payload === "string" ? { name: payload } : payload;
      return HttpClient_PermissionsModule.put<any>(
        `${API_ENDPOINTS.PERMISSIONS_ROLES}/${id}`,
        body
      );
    },
    deleteRole: (id: number | string) =>
      HttpClient_PermissionsModule.delete<any>(`${API_ENDPOINTS.PERMISSIONS_ROLES}/${id}`),
    getRolePermissionsDetails: async (roleId: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(`${API_ENDPOINTS.PERMISSIONS_MANAGE_ROLES}/${roleId}`);
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch role permissions"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData?.data || resData;
    },
    saveRolePermissions: (payload: { role_id: number; permissions: any[] }) =>
      HttpClient_PermissionsModule.post<any>(API_ENDPOINTS.PERMISSIONS_MANAGE_ROLES, payload),
  };
  User = {
    getActivityLogs: (input: any) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.GET_USER_ACTIVITY_LOGS}`,
        input,
      );
    },
    getActivityLogActions: (input: any) => {
      return HttpClient_EmailTemplate.get<any>(
        `${API_ENDPOINTS.GET_USER_ACTIVITY_LOG_ACTIONS}`,
        input,
      );
    },
  };
  whiteLabelAdmin = {
    getAdmins: async (
      page: number = 1,
      limit: number = 10,
      search_value?: string,
      search_field: string = "first_name"
    ) => {
      const params: any = { page, limit };
      if (search_value && search_value.trim() !== "") {
        params.search_value = search_value.trim();
        params.search_field = search_field;
      }
      const resData = await HttpClient_PermissionsModule.get<any>(
        API_ENDPOINTS.WHITE_LABEL_ADMIN_LIST,
        params
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch admin list"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      let list: WhiteLabelAdminItem[] = [];
      if (Array.isArray(resData?.items)) {
        list = resData.items;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      const total =
        Number(resData?.totalcount) ||
        Number(resData?.total) ||
        Number(resData?.data?.totalcount) ||
        list.length;
      return { data: list, total, totalPages: resData?.totalPages || 1 };
    },
    getAdminById: async (id: number | string) => {
      const resData = await HttpClient_PermissionsModule.get<any>(
        API_ENDPOINTS.WHITE_LABEL_ADMIN_DETAILS(id)
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to fetch admin details"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData?.data || resData;
    },
    createAdmin: (payload: CreateWhiteLabelAdminInput) => {
      return HttpClient_PermissionsModule.post<any>(
        API_ENDPOINTS.WHITE_LABEL_ADMIN_ADD,
        payload
      );
    },
    updateAdmin: (id: number | string, payload: UpdateWhiteLabelAdminInput) => {
      return HttpClient_PermissionsModule.put<any>(
        API_ENDPOINTS.WHITE_LABEL_ADMIN_UPDATE(id),
        payload
      );
    },
    deleteAdmin: async (id: number | string) => {
      const resData = await HttpClient_PermissionsModule.delete<any>(
        API_ENDPOINTS.WHITE_LABEL_ADMIN_REMOVE(id)
      );
      if (
        resData?.status === "failure" ||
        (typeof resData?.code === "number" && resData.code >= 400)
      ) {
        const error: any = new Error(
          resData?.message || resData?.error_description || "Failed to delete admin"
        );
        error.response = { data: resData, status: resData?.code || 400 };
        throw error;
      }
      return resData;
    },
  };
}

export default new Client();
